// Shader materials for the 3D viewer. Everything is drawn in sRGB directly
// (no tone mapping) so palette colours match the 2D map exactly.
import * as THREE from "three";
import { CONTEXT_RELIEF, KM_PER_DEGREE_LATITUDE, KM_PER_DEGREE_LONGITUDE, ORIGIN } from "./geo.js";

export const MAX_LEVELS = 64;

const COMMON = /* glsl */`
  uniform vec2 uOrigin;
  uniform vec2 uKm;
  uniform sampler2D uBed;
  uniform vec4 uBedGrid;
  uniform vec2 uBedSize;
  uniform float uLakeId;
  uniform vec4 uCut;
  uniform int uCutLakes;
  uniform vec2 uWater;

  // Haze for a camera under the surface: it thickens with the distance to what is seen.
  vec3 underwaterFog(vec3 color, vec3 world) {
    float haze = uWater.x * (1.0 - exp(-uWater.y * distance(cameraPosition, world)));
    return mix(color, vec3(0.045, 0.235, 0.34), haze);
  }

  vec2 latLon(vec3 local) {
    return vec2(uOrigin.x - local.z / uKm.x, uOrigin.y + local.x / uKm.y);
  }
  vec2 bedUv(vec2 ll) {
    return vec2(((ll.y - uBedGrid.y) / uBedGrid.w + 0.5) / uBedSize.x, ((ll.x - uBedGrid.x) / uBedGrid.z + 0.5) / uBedSize.y);
  }
  vec2 bedAt(vec2 ll) { return texture(uBed, bedUv(ll)).rg; }
  // Only lakes crossed by a loaded curtain are cut, not their neighbours.
  bool cutAway(vec3 local) {
    int lake = int(floor(bedAt(latLon(local)).g + 0.5));
    return uCut.w > 0.5 && (uCutLakes & (1 << lake)) != 0
      && dot(local.xz - uCut.xy, vec2(-uCut.z, 1.0)) * sign(uCut.w - 1.5) > 0.0;
  }
  bool otherLake(vec2 bed) { return uLakeId > 0.5 && abs(bed.g - uLakeId) > 0.5; }
  float lineMask(float value, float width) {
    float f = abs(fract(value - 0.5) - 0.5) / max(fwidth(value), 1e-5);
    return 1.0 - min(f / width, 1.0);
  }
`;

const VOLUME = /* glsl */`
  uniform highp sampler3D uVolume;
  uniform vec4 uVolumeGrid;
  uniform vec3 uVolumeSize;
  uniform float uDepths[${MAX_LEVELS}];
  uniform int uLevels;
  uniform sampler2D uPalette;
  uniform vec2 uRange;

  float levelPosition(float depth) {
    if (depth <= uDepths[0]) return 0.0;
    for (int index = 1; index < ${MAX_LEVELS}; index++) {
      if (index >= uLevels) break;
      if (depth <= uDepths[index]) {
        return float(index - 1) + (depth - uDepths[index - 1]) / (uDepths[index] - uDepths[index - 1]);
      }
    }
    return float(uLevels - 1);
  }
  vec2 volumeUv(vec2 ll) {
    return vec2(((ll.y - uVolumeGrid.y) / uVolumeGrid.w + 0.5) / uVolumeSize.x, ((ll.x - uVolumeGrid.x) / uVolumeGrid.z + 0.5) / uVolumeSize.y);
  }
  bool outsideVolume(vec2 ll) {
    vec2 uv = volumeUv(ll);
    return any(lessThan(uv, vec2(0.0))) || any(greaterThan(uv, vec2(1.0)));
  }
  float temperatureAt(vec2 ll, float depth) {
    return texture(uVolume, vec3(volumeUv(ll), (levelPosition(depth) + 0.5) / uVolumeSize.z)).r;
  }
  vec3 temperatureColor(float value) {
    float position = clamp((value - uRange.x) / max(uRange.y - uRange.x, 1e-3), 0.0, 1.0);
    return texture(uPalette, vec2(position, 0.5)).rgb;
  }
`;

const LOCAL_VERTEX = /* glsl */`
  out vec3 vLocal;
  out vec3 vWorld;
  void main() {
    vLocal = position;
    vec4 world = modelMatrix * vec4(position, 1.0);
    vWorld = world.xyz;
    gl_Position = projectionMatrix * viewMatrix * world;
  }
`;

export function sharedUniforms(bedTexture, field) {
  return {
    uOrigin: { value: new THREE.Vector2(ORIGIN.latitude, ORIGIN.longitude) },
    uKm: { value: new THREE.Vector2(KM_PER_DEGREE_LATITUDE, KM_PER_DEGREE_LONGITUDE) },
    uBed: { value: bedTexture },
    uBedGrid: { value: new THREE.Vector4(field.south, field.west, field.cellLatitude, field.cellLongitude) },
    uBedSize: { value: new THREE.Vector2(field.columns, field.rows) },
    uLakeId: { value: 0 },
    // x, z of a point on the cut line, the line's slope dz/dx, and the side to
    // drop (0 off, 1 or 2 for each side). Lines are stored as z = z0 + slope (x - x0).
    uCut: { value: new THREE.Vector4(0, 0, 0, 0) },
    uCutLakes: { value: 0 },
    // How far under the surface the camera is (0 above, 1 well below), and the haze per km of sight.
    uWater: { value: new THREE.Vector2(0, 0.1) }
  };
}

export function volumeUniforms() {
  return {
    uVolume: { value: null },
    uVolumeGrid: { value: new THREE.Vector4() },
    uVolumeSize: { value: new THREE.Vector3(1, 1, 1) },
    uDepths: { value: new Array(MAX_LEVELS).fill(0) },
    uLevels: { value: 1 },
    uPalette: { value: null },
    uRange: { value: new THREE.Vector2(0, 30) }
  };
}

// Lighting and contour settings shared by the lakebed and the depth slice.
export function reliefUniforms() {
  return {
    uDepthPalette: { value: null },
    uContours: { value: 1 },
    uContourInterval: { value: 20 },
    uLight: { value: new THREE.Vector3(-0.45, 0.8, -0.4).normalize() },
    uFocus: { value: 0 },
    uExaggeration: { value: 1 }
  };
}

function material(options) {
  return new THREE.ShaderMaterial({ glslVersion: THREE.GLSL3, toneMapped: false, ...options });
}

// Hillshade of the lakebed from the bed texture, as seen from the north-west.
// 1.0 is flat ground; slopes facing the light are brighter.
const RELIEF = /* glsl */`
  uniform float uExaggeration;
  uniform vec3 uLight;
  uniform float uContours;
  uniform float uContourInterval;

  float bedHeight(vec2 uv) { return max(texture(uBed, uv).r, 0.0); }

  float bedShade(vec2 uv) {
    vec2 texel = 1.0 / uBedSize;
    vec2 spacing = vec2(uBedGrid.w * uKm.y, uBedGrid.z * uKm.x);
    float east = bedHeight(uv + vec2(texel.x, 0.0));
    float west = bedHeight(uv - vec2(texel.x, 0.0));
    float north = bedHeight(uv + vec2(0.0, texel.y));
    float south = bedHeight(uv - vec2(0.0, texel.y));
    vec2 slope = uExaggeration / 1000.0 * vec2(east - west, south - north) / (2.0 * spacing);
    vec3 normal = normalize(vec3(slope.x, 1.0, slope.y));
    return clamp(dot(normal, uLight) / max(uLight.y, 0.1), 0.0, 2.0);
  }
`;

const DEPTH_RAMP = /* glsl */`
  uniform sampler2D uDepthPalette;
  vec3 depthRamp(float depth) {
    float t = clamp(sqrt(max(depth, 0.0) / 400.0), 0.0, 1.0);
    return texture(uDepthPalette, vec2(t, 0.5)).rgb;
  }
`;

// One lakebed mesh per lake, so the lakes around the focused one can be
// flattened and dimmed to stay out of the way.
export function terrainMaterial(shared, relief, meshLake) {
  return material({
    uniforms: { ...shared, ...relief, uMeshLake: { value: meshLake }, uContextRelief: { value: CONTEXT_RELIEF } },
    vertexShader: /* glsl */`
      in float aDepth;
      uniform float uExaggeration;
      uniform float uFocus;
      uniform float uLakeId;
      uniform float uMeshLake;
      uniform float uContextRelief;
      out float vDepth;
      out float vContext;
      out vec3 vNormal;
      out vec3 vLocal;
      out vec3 vWorld;
      void main() {
        vContext = (uFocus > 0.5 && abs(uMeshLake - uLakeId) > 0.5) ? 1.0 : 0.0;
        float relief = mix(1.0, uContextRelief, vContext);
        vDepth = aDepth;
        vLocal = position;
        // The depth group only scales y, so its inverse-transpose scales normals' y by 1/exaggeration.
        vNormal = normalize(vec3(normal.x, normal.y / (uExaggeration * relief), normal.z));
        vec4 world = modelMatrix * vec4(position.x, position.y * relief, position.z, 1.0);
        vWorld = world.xyz;
        gl_Position = projectionMatrix * viewMatrix * world;
      }
    `,
    fragmentShader: /* glsl */`
      ${COMMON}
      ${DEPTH_RAMP}
      uniform float uContours;
      uniform float uContourInterval;
      uniform vec3 uLight;
      uniform float uFocus;
      in float vDepth;
      in float vContext;
      in vec3 vNormal;
      in vec3 vLocal;
      in vec3 vWorld;
      out vec4 outColor;
      void main() {
        if (vDepth <= 0.0) discard;
        vec3 normal = normalize(vNormal);
        if (!gl_FrontFacing) normal = -normal;
        float diffuse = max(dot(normal, uLight), 0.0);
        float sky = 0.5 + 0.5 * normal.y;
        vec3 color = depthRamp(vDepth) * (0.3 + 0.36 * sky + 0.62 * diffuse);
        float quiet = vContext;
        if (uFocus > 0.5 && quiet < 0.5 && otherLake(bedAt(latLon(vLocal)))) quiet = 1.0;
        float minor = lineMask(vDepth / uContourInterval, 1.0);
        float major = lineMask(vDepth / (uContourInterval * 5.0), 1.3);
        float contour = uContours > 0.5 ? max(minor * 0.2, major * 0.46) : 0.0;
        color = mix(color, vec3(0.86, 0.96, 1.0), contour * (1.0 - quiet));
        if (quiet > 0.5) {
          float grey = dot(color, vec3(0.3, 0.59, 0.11));
          color = mix(vec3(grey), color, 0.5) * 0.44 + vec3(0.012, 0.026, 0.04);
        } else if (cutAway(vLocal)) {
          color = mix(color, vec3(0.06, 0.1, 0.14), 0.6);
        }
        outColor = vec4(underwaterFog(color, vWorld), 1.0);
      }
    `,
    side: THREE.DoubleSide
  });
}

export function landMaterial(shared, halo) {
  return material({
    uniforms: { ...shared, uHalo: { value: halo } },
    vertexShader: LOCAL_VERTEX,
    fragmentShader: /* glsl */`
      ${COMMON}
      uniform sampler2D uHalo;
      in vec3 vLocal;
      out vec4 outColor;
      void main() {
        vec2 ll = latLon(vLocal);
        vec2 uv = bedUv(ll);
        float depth = texture(uBed, uv).r;
        if (depth > 0.0) discard;
        float glow = texture(uHalo, uv).r;
        vec3 color = vec3(0.062, 0.1, 0.136) + vec3(0.046, 0.082, 0.1) * glow;
        float graticule = max(lineMask(ll.x, 0.7), lineMask(ll.y, 0.7));
        color += vec3(0.04, 0.055, 0.065) * graticule * (0.3 + 0.7 * glow);
        float shore = 1.0 - smoothstep(0.0, fwidth(depth) * 1.6 + 1e-4, abs(depth));
        color = mix(color, vec3(0.62, 0.87, 0.92), shore * 0.85);
        // The map fades into the page backdrop instead of ending in a hard edge.
        vec2 edge = min(uv, 1.0 - uv) * uBedSize / max(uBedSize.x, uBedSize.y);
        float alpha = smoothstep(0.0, 0.1, min(edge.x, edge.y));
        outColor = vec4(color * alpha, alpha);
      }
    `
  });
}

export function waterSurfaceMaterial(shared) {
  return material({
    uniforms: { ...shared, uOpacity: { value: 0.1 } },
    vertexShader: /* glsl */`
      out vec3 vLocal;
      out vec3 vWorld;
      void main() {
        vLocal = position;
        vec4 world = modelMatrix * vec4(position, 1.0);
        vWorld = world.xyz;
        gl_Position = projectionMatrix * viewMatrix * world;
      }
    `,
    fragmentShader: /* glsl */`
      ${COMMON}
      uniform float uOpacity;
      in vec3 vLocal;
      in vec3 vWorld;
      out vec4 outColor;
      void main() {
        vec2 bed = bedAt(latLon(vLocal));
        float depth = bed.r;
        if (depth <= 0.0 || cutAway(vLocal)) discard;
        vec3 view = normalize(cameraPosition - vWorld);
        if (cameraPosition.y < 0.0) {
          // From below the surface is a ceiling: bright where it opens onto the sky
          // overhead, and a dim mirror of the water around it elsewhere.
          float window = smoothstep(0.55, 0.8, -view.y);
          vec3 ceiling = mix(vec3(0.07, 0.3, 0.42), vec3(0.6, 0.88, 0.96), window);
          outColor = vec4(underwaterFog(ceiling, vWorld), mix(0.5, 0.34, window) * uWater.x);
          return;
        }
        float fresnel = pow(1.0 - max(view.y, 0.0), 3.0);
        float edge = 1.0 - smoothstep(0.0, fwidth(depth) * 2.0 + 1e-4, depth);
        vec3 color = mix(vec3(0.32, 0.62, 0.78), vec3(0.75, 0.9, 0.98), fresnel);
        outColor = vec4(color, clamp(uOpacity + fresnel * 0.22 + edge * 0.25, 0.0, 0.6));
      }
    `,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide
  });
}

const ISOTHERM_LINES = /* glsl */`
  uniform float uIsothermStep;
  uniform float uTarget;
  uniform float uShowTarget;
  vec3 withIsotherms(vec3 color, float value) {
    color = mix(color, color * 0.62, lineMask(value / uIsothermStep, 0.9) * 0.4);
    if (uShowTarget > 0.5) {
      float target = 1.0 - min(abs(value - uTarget) / max(fwidth(value), 1e-4) / 1.4, 1.0);
      color = mix(color, vec3(1.0), target * 0.95);
    }
    return color;
  }
`;

function isothermUniforms() {
  return { uIsothermStep: { value: 1 }, uTarget: { value: 10 }, uShowTarget: { value: 0 } };
}

export function sliceMaterial(shared, volume, relief) {
  return material({
    uniforms: { ...shared, ...volume, ...relief, ...isothermUniforms(), uSliceDepth: { value: 0 } },
    vertexShader: LOCAL_VERTEX,
    fragmentShader: /* glsl */`
      ${COMMON}
      ${VOLUME}
      ${ISOTHERM_LINES}
      ${RELIEF}
      uniform float uSliceDepth;
      in vec3 vLocal;
      in vec3 vWorld;
      out vec4 outColor;
      void main() {
        vec2 ll = latLon(vLocal);
        vec2 bed = bedAt(ll);
        if (bed.r <= uSliceDepth + 0.05 || otherLake(bed) || outsideVolume(ll) || cutAway(vLocal)) discard;
        float value = temperatureAt(ll, uSliceDepth);
        vec3 color = withIsotherms(temperatureColor(value), value);
        // Shade by the lakebed below so the basin still reads through the temperature colours.
        float shade = clamp(mix(1.0, bedShade(bedUv(ll)), 0.55), 0.7, 1.28);
        float deep = smoothstep(0.0, 300.0, bed.r - uSliceDepth);
        color *= shade * (1.03 - 0.17 * deep);
        float contour = uContours > 0.5 ? lineMask(bed.r / uContourInterval, 1.0) : 0.0;
        color = mix(color, vec3(1.0), contour * 0.1);
        float rim = 1.0 - smoothstep(0.0, fwidth(bed.r) * 2.0 + 1e-4, bed.r - uSliceDepth);
        outColor = vec4(underwaterFog(mix(color, vec3(0.95), rim * 0.6), vWorld), 1.0);
      }
    `,
    side: THREE.DoubleSide
  });
}

export function curtainMaterial(shared, volume) {
  return material({
    uniforms: { ...shared, ...volume, ...isothermUniforms(), uGridStep: { value: 10 } },
    vertexShader: LOCAL_VERTEX,
    fragmentShader: /* glsl */`
      ${COMMON}
      ${VOLUME}
      ${ISOTHERM_LINES}
      uniform float uGridStep;
      in vec3 vLocal;
      in vec3 vWorld;
      out vec4 outColor;
      void main() {
        vec2 ll = latLon(vLocal);
        float depth = -vLocal.y * 1000.0;
        vec2 bed = bedAt(ll);
        if (depth < 0.0 || depth >= bed.r || otherLake(bed) || outsideVolume(ll)) discard;
        float value = temperatureAt(ll, depth);
        vec3 color = withIsotherms(temperatureColor(value), value);
        color = mix(color, color * 0.7, lineMask(depth / uGridStep, 0.8) * 0.5);
        float floorLine = 1.0 - smoothstep(0.0, fwidth(depth) * 2.0, bed.r - depth);
        float topLine = 1.0 - smoothstep(0.0, fwidth(depth) * 2.0, depth);
        color = mix(color, vec3(0.96), topLine * 0.7);
        outColor = vec4(underwaterFog(mix(color, vec3(0.08, 0.1, 0.12), floorLine), vWorld), 1.0);
      }
    `,
    side: THREE.DoubleSide
  });
}

export function isothermSurfaceMaterial(shared, volume) {
  return material({
    uniforms: {
      ...shared,
      ...volume,
      uSurface: { value: null },
      uTargetColor: { value: new THREE.Color(1, 1, 1) },
      // Depths (m) that map to the light and dark ends of the surface's depth shading.
      uDepthRange: { value: new THREE.Vector2(0, 100) },
      uContourInterval: { value: 5 },
      uLight: { value: new THREE.Vector3(-0.45, 0.8, -0.4).normalize() }
    },
    vertexShader: /* glsl */`
      uniform vec2 uOrigin;
      uniform vec2 uKm;
      uniform sampler2D uSurface;
      uniform vec4 uVolumeGrid;
      uniform vec3 uVolumeSize;
      out vec3 vLocal;
      out vec3 vWorld;
      out float vDepth;
      void main() {
        vec2 ll = vec2(uOrigin.x - position.z / uKm.x, uOrigin.y + position.x / uKm.y);
        vec2 uv = vec2(((ll.y - uVolumeGrid.y) / uVolumeGrid.w + 0.5) / uVolumeSize.x, ((ll.x - uVolumeGrid.x) / uVolumeGrid.z + 0.5) / uVolumeSize.y);
        float depth = textureLod(uSurface, uv, 0.0).r;
        vec3 displaced = vec3(position.x, -depth / 1000.0, position.z);
        vLocal = displaced;
        vDepth = depth;
        vec4 world = modelMatrix * vec4(displaced, 1.0);
        vWorld = world.xyz;
        gl_Position = projectionMatrix * viewMatrix * world;
      }
    `,
    fragmentShader: /* glsl */`
      ${COMMON}
      ${VOLUME}
      uniform sampler2D uSurface;
      uniform vec3 uTargetColor;
      uniform vec2 uDepthRange;
      uniform float uContourInterval;
      uniform vec3 uLight;
      in vec3 vLocal;
      in vec3 vWorld;
      in float vDepth;
      out vec4 outColor;
      void main() {
        vec2 ll = latLon(vLocal);
        if (outsideVolume(ll)) discard;
        vec2 surface = texture(uSurface, volumeUv(ll)).rg;
        vec2 bed = bedAt(ll);
        if (surface.g < 0.5 || otherLake(bed) || vDepth >= bed.r - 0.3 || cutAway(vLocal)) discard;
        vec3 normal = normalize(cross(dFdx(vWorld), dFdy(vWorld)));
        if (normal.y < 0.0) normal = -normal;
        float light = 0.62 + 0.52 * max(dot(normal, uLight), 0.0);
        // Shallow water is pale, deep water dark: the shading is the depth of the target temperature.
        float depthT = clamp((vDepth - uDepthRange.x) / max(uDepthRange.y - uDepthRange.x, 1.0), 0.0, 1.0);
        vec3 pale = mix(uTargetColor, vec3(1.0), 0.55);
        vec3 dark = uTargetColor * vec3(0.26, 0.3, 0.42);
        vec3 tint = mix(mix(pale, uTargetColor, clamp(depthT * 2.0, 0.0, 1.0)), dark, clamp(depthT * 2.0 - 1.0, 0.0, 1.0));
        vec3 color = tint * light;
        color = mix(color, vec3(1.0), lineMask(vDepth / uContourInterval, 1.0) * 0.4);
        outColor = vec4(underwaterFog(color, vWorld), 1.0);
      }
    `,
    side: THREE.DoubleSide
  });
}
